import React, { useMemo } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Typography from '@mui/material/Typography';
import { Card, Box, ListSubheader, Tooltip } from '@mui/material';
import { CircularProgressWithLabel } from '../../shared/Spinner/Spinner';
import CustomAvatar from '../Profile/CustomAvatar';
import { adminCardSx, adminCardWrapperSx, adminListScrollSx } from './adminStyles';
import { Avatar } from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';

function UserListComponent({
  clickCallBack,
  users,
  addUserButton,
  getAvatarLetter,
  getListItemTitle,
  getListItemSubTitle,
  editingUserPrivilege,
  requiredEditingUserPrivilege,
}) {
  const usersList = useMemo(
    () =>
      [...(users ?? [])].sort((a, b) => {
        // 'username' in for when component is accessed from Admin Page. 'organizationName' for when it is accessed
        // from Broadsight Admin Page
        const userA = (a.username ?? a.organizationName ?? '').toLowerCase();
        const userB = (b.username ?? b.organizationName ?? '').toLowerCase();
        if (userA < userB) return -1;
        if (userA > userB) return 1;
        return 0;
      }),
    [users]
  );

  // Enabled and disabled users are for Admin Page.
  const enabledUsers = usersList?.filter((user) => user.enabled === 'True') ?? [];
  const disabledUsers = usersList?.filter((user) => user.enabled === 'False') ?? [];

  return (
    <Box sx={adminCardWrapperSx}>
      <Card sx={adminCardSx}>
        <List
          sx={{
            ...adminListScrollSx,
            width: '100%',
          }}
          disablePadding={true}
          padding={0}
        >
          {editingUserPrivilege >= requiredEditingUserPrivilege && addUserButton()}

          {enabledUsers.length <= 0 &&
            disabledUsers.length <= 0 &&
            usersList.length > 0 &&
            usersList.map((user, index) => {
              user.id = user._id;
              return (
                <ListItem
                  key={index}
                  component="div"
                  button={true}
                  onClick={() => clickCallBack(user)}
                  sx={user.status === 1 || user.status === 3 ? { color: 'primaryDarker.main' } : {}}
                >
                  <ListItemAvatar>
                    {user.username ? <CustomAvatar user={user} /> : <Avatar>{getAvatarLetter(user)}</Avatar>}
                  </ListItemAvatar>
                  <ListItemText
                    primary={getListItemTitle(user)}
                    secondary={
                      <React.Fragment>
                        <Typography
                          sx={{
                            display: 'inline',
                            ...(user.status === 1 || user.status === 3 ? { color: 'primaryDarker.main' } : {}),
                          }}
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          {getListItemSubTitle(user)}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </ListItem>
              );
            })}

          {enabledUsers.length > 0 &&
            enabledUsers.map((user, index) => {
              const isMFAConfigured =
                user.mfaEnabled === 'Enabled' && user.phone !== 'Not Configured' && user.mfaSettings.length > 0;
              user.id = user._id;
              let toolTipTitle;

              if (user.status === 3) {
                toolTipTitle = "User can reset their password with the 'Forgot your password?' option on login";
              } else {
                toolTipTitle = 'User is still in the middle of the password change process';
              }

              return (
                <ListItem key={index} component="div" button={true} onClick={() => clickCallBack(user)}>
                  <ListItemAvatar>
                    {user.username ? <CustomAvatar user={user} /> : <Avatar>{getAvatarLetter(user)}</Avatar>}
                  </ListItemAvatar>
                  <ListItemText
                    sx={user.status === 1 || user.status === 3 ? { color: 'primaryDarker.main' } : {}}
                    primary={
                      <React.Fragment>
                        <Tooltip title={toolTipTitle} placement="bottom-start" disableHoverListener={user.status === 2}>
                          <Typography>{getListItemTitle(user)}</Typography>
                        </Tooltip>
                      </React.Fragment>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography
                          sx={{ display: 'inline', opacity: 0.6 }}
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          {getListItemSubTitle(user)}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                  {isMFAConfigured && (
                    <Tooltip title="MFA is configured for this user">
                      <KeyIcon />
                    </Tooltip>
                  )}
                </ListItem>
              );
            })}

          {disabledUsers.length > 0 && (
            <ListSubheader
              sx={{
                backgroundColor: (theme) => theme.palette.whiteScaleLighter.main,
                color: 'text.secondary',
                width: 'calc(100% - 1.5rem)',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 1,
                mb: 0.75,
                mx: 0.75,
                py: 0.35,
              }}
            >
              Deactivated
            </ListSubheader>
          )}

          {disabledUsers.length > 0 &&
            disabledUsers.map((user, index) => {
              user.id = user._id;
              return (
                <ListItem key={index} component="div" button={true} onClick={() => clickCallBack(user)}>
                  <ListItemAvatar>
                    {user.username ? <CustomAvatar user={user} /> : <Avatar>{getAvatarLetter(user)}</Avatar>}
                  </ListItemAvatar>
                  <ListItemText
                    primary={getListItemTitle(user)}
                    secondary={
                      <React.Fragment>
                        <Typography
                          sx={{ display: 'inline', opacity: 0.6 }}
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          {getListItemSubTitle(user)}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </ListItem>
              );
            })}

          {!users && <CircularProgressWithLabel></CircularProgressWithLabel>}
        </List>
      </Card>
    </Box>
  );
}

export default UserListComponent;
